/**
 * Measurement coverage — "how much of what you are looking at is actually
 * known?"
 *
 * ---------------------------------------------------------------------------
 * WHY THIS EXISTS
 * ---------------------------------------------------------------------------
 * A page can be full of green ticks and still be mostly ignorance. `/databases`
 * renders eleven clusters; if six of their backup signals are `unproven` and
 * ProxySQL is `blocked`, the page is honest chip-by-chip and yet the overall
 * impression — lots of cards, mostly calm — is a lie of composition. The
 * per-signal honesty that `lib/signal.ts` guarantees does not aggregate by
 * itself.
 *
 * So every page carries one line that aggregates it: of the N measurements on
 * this page, how many were measured, how many legitimately matched nothing, how
 * many were not measured, how many cannot be measured, and how many errored.
 *
 * ---------------------------------------------------------------------------
 * THIS INVENTS NOTHING
 * ---------------------------------------------------------------------------
 * It is a pure function of the payload already on screen. No new endpoint, no
 * new field, no second request, no derived "health score". If the API returns
 * fewer signals tomorrow the denominator shrinks and the bar says so, which is
 * the correct behaviour: coverage is a property of the response, not of the
 * cluster.
 */

// `.js`, not extensionless: this module is compiled BOTH by Vite (bundler
// resolution) and by `tsconfig.test.json` (node16), because `test/ui/**` imports
// it directly to assert on the tally. node16 requires the extension.
import type { Epistemic } from './signal.js';

/**
 * The statuses a real signal can carry. An object is only counted when its
 * `status` is one of these — which is what stops `FindingView.status`
 * (`new`/`known`/`escalated`/`resolved`) and the `filters.status` echo from
 * being mistaken for measurements. `StatusRow` dodges this too, but by a better
 * route: the API deliberately names its HTTP status `httpStatus` precisely so
 * that a payload walk like this one cannot trip over it (`api/types.ts`).
 */
const SIGNAL_VOCABULARY = new Set([
  'ok',
  'empty',
  'unproven',
  'blocked',
  'error',
  'above_top_bucket',
  'below_request_floor',
]);

/**
 * The companion keys a real signal carries alongside its discriminant. A bare
 * `{ status: 'ok' }` with nothing else is not a measurement and is not counted;
 * every genuine member of the union has at least one of these.
 */
const COMPANION_KEYS = [
  'value',
  'reason',
  'detail',
  'gate',
  'kind',
  'message',
  'seconds',
  'quantile',
  'requests',
  'minRequests',
  'topBucketLe',
];

export interface Coverage {
  /** `ok` — a reading exists. */
  ok: number;
  /** `empty` — gate-proven, legitimately nothing. Measured. NOT zero. */
  empty: number;
  /** `unproven` — we did not measure, and emptiness would say nothing. */
  unproven: number;
  /** `blocked` — cannot be measured today. Terminal. */
  blocked: number;
  /** `error` — the collector failed. */
  error: number;
  /** The two percentile edges. Measured, and neither is a number. */
  edge: number;
  total: number;
}

export const EMPTY_COVERAGE: Coverage = {
  ok: 0,
  empty: 0,
  unproven: 0,
  blocked: 0,
  error: 0,
  edge: 0,
  total: 0,
};

function isSignalLike(value: unknown): value is { status: string } {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return false;
  const record = value as Record<string, unknown>;
  const status = record['status'];
  if (typeof status !== 'string' || !SIGNAL_VOCABULARY.has(status)) return false;
  return COMPANION_KEYS.some((key) => key in record);
}

/**
 * Tally every signal in a payload, at any depth.
 *
 * It descends THROUGH an `ok` signal into its value, because the nested things
 * are separate measurements: `nodes` being `ok` says the node list was read, and
 * says nothing about whether each node's `requests` was. Both are counted.
 *
 * Depth is capped and a visited-set guards cycles — this runs on every render of
 * every page, over a payload that can carry 330 pods.
 */
export function coverageOf(payload: unknown): Coverage {
  const counts: Coverage = { ...EMPTY_COVERAGE };
  const seen = new WeakSet<object>();

  const visit = (node: unknown, depth: number): void => {
    if (depth > 24 || node === null || typeof node !== 'object') return;
    if (seen.has(node)) return;
    seen.add(node);

    if (Array.isArray(node)) {
      for (const item of node) visit(item, depth + 1);
      return;
    }

    if (isSignalLike(node)) {
      counts.total += 1;
      switch (node.status) {
        case 'ok':
          counts.ok += 1;
          break;
        case 'empty':
          counts.empty += 1;
          break;
        case 'unproven':
          counts.unproven += 1;
          break;
        case 'blocked':
          counts.blocked += 1;
          break;
        case 'error':
          counts.error += 1;
          break;
        default:
          // above_top_bucket / below_request_floor: measured, not a number.
          counts.edge += 1;
          break;
      }
    }

    for (const value of Object.values(node as Record<string, unknown>)) {
      visit(value, depth + 1);
    }
  };

  visit(payload, 0);
  return counts;
}

/** The three epistemic buckets, for the segmented bar. */
export function byEpistemic(coverage: Coverage): Record<Epistemic, number> {
  return {
    measured: coverage.ok + coverage.empty + coverage.edge,
    unmeasured: coverage.unproven + coverage.error,
    unmeasurable: coverage.blocked,
  };
}

/**
 * The sentence a screen reader hears, and the sentence printed under the bar.
 *
 * It leads with the number that matters — how much we do NOT know — because
 * that is the figure a dashboard normally hides and the one an operator has to
 * discount every other reading against.
 */
export function coverageSentence(coverage: Coverage): string {
  if (coverage.total === 0) return 'This page carries no measured signals.';
  const buckets = byEpistemic(coverage);
  const unknown = buckets.unmeasured + buckets.unmeasurable;
  const parts: string[] = [];
  if (coverage.ok > 0) parts.push(`${String(coverage.ok)} read`);
  if (coverage.empty > 0) parts.push(`${String(coverage.empty)} gate-proven empty`);
  if (coverage.edge > 0) parts.push(`${String(coverage.edge)} off-scale`);
  if (coverage.unproven > 0) parts.push(`${String(coverage.unproven)} unproven`);
  if (coverage.error > 0) parts.push(`${String(coverage.error)} errored`);
  if (coverage.blocked > 0) parts.push(`${String(coverage.blocked)} blocked`);
  const lead =
    unknown === 0
      ? `All ${String(coverage.total)} measurements on this page were made.`
      : `${String(unknown)} of ${String(coverage.total)} measurements on this page were NOT made.`;
  return `${lead} ${parts.join(', ')}.`;
}

/** Percentage of a bucket, for the bar widths. Never rounded up to 100. */
export function coveragePercent(part: number, total: number): number {
  if (total <= 0) return 0;
  return (part / total) * 100;
}
